import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, ShieldOff, Inbox, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const todayKey = () => `dashboard-summary-dismissed-${new Date().toISOString().slice(0, 10)}`;

// Admin-only "login-day" overview — surfaces the three things worth a
// glance before diving into a tab: regions missing an owner, today's Safe
// Test Mode suppressions, and pending Agent lead requests. Dismissible for
// the rest of the calendar day (Manila-agnostic, local browser date).
const AdminDashboardSummary = ({ onGoToRouting, onGoToLeadRequests }) => {
  const [summary, setSummary] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(todayKey()) === '1');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/leads/dashboard-summary`, { withCredentials: true })
      .then((res) => setSummary(res.data))
      .catch(() => {});
  }, []);

  const dismiss = () => {
    localStorage.setItem(todayKey(), '1');
    setDismissed(true);
  };

  if (dismissed || !summary) return null;
  const { region_alerts = [], suppressed_today = 0, pending_lead_requests = 0 } = summary;
  if (region_alerts.length === 0 && suppressed_today === 0 && pending_lead_requests === 0) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      <div
        className="bg-slate-900 text-white rounded-xl px-4 py-3 flex flex-wrap items-center gap-4"
        data-testid="admin-dashboard-summary"
      >
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 flex-shrink-0">Today</span>
        {region_alerts.length > 0 && (
          <button
            type="button"
            onClick={onGoToRouting}
            data-testid="dashboard-summary-region-alerts"
            className="inline-flex items-center gap-1.5 text-sm hover:underline"
          >
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
            {region_alerts.length} region{region_alerts.length === 1 ? '' : 's'} need{region_alerts.length === 1 ? 's' : ''} an owner
          </button>
        )}
        {suppressed_today > 0 && (
          <span data-testid="dashboard-summary-suppressed" className="inline-flex items-center gap-1.5 text-sm text-slate-200">
            <ShieldOff className="w-4 h-4 text-purple-400 flex-shrink-0" />
            {suppressed_today} send{suppressed_today === 1 ? '' : 's'} suppressed
          </span>
        )}
        {pending_lead_requests > 0 && (
          <button
            type="button"
            onClick={onGoToLeadRequests}
            data-testid="dashboard-summary-pending-requests"
            className="inline-flex items-center gap-1.5 text-sm hover:underline"
          >
            <Inbox className="w-4 h-4 text-sky-400 flex-shrink-0" />
            {pending_lead_requests} pending lead request{pending_lead_requests === 1 ? '' : 's'}
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          data-testid="dashboard-summary-dismiss"
          className="ml-auto text-slate-400 hover:text-white flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default AdminDashboardSummary;
